import React, { useState, useMemo } from 'react';

const today = () => new Date().toISOString().split('T')[0];

const AdminFinancialsDepositModal = ({ orders, onConfirm, onClose }) => {
  const [depositDate, setDepositDate] = useState(today());
  const [checkedIds, setCheckedIds] = useState(new Set(orders.map(o => o.order_id)));

  const sorted = useMemo(() =>
    [...orders].sort((a, b) => (a.title_company || '').localeCompare(b.title_company || '')),
    [orders]
  );

  const toggle = (id) => {
    const next = new Set(checkedIds);
    next.has(id) ? next.delete(id) : next.add(id);
    setCheckedIds(next);
  };

  const selected = sorted.filter(o => checkedIds.has(o.order_id));
  const total = selected.reduce((sum, o) => sum + (o.ar_amount || 0), 0);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      <div className="bg-white rounded-2xl shadow-xl border border-neutral-200 w-full max-w-lg">
        <div className="flex items-center justify-between px-6 py-4 border-b border-neutral-200">
          <h2 className="text-base font-semibold text-neutral-800">Create Bank Deposit</h2>
          <button onClick={onClose} className="text-neutral-400 hover:text-neutral-600 text-xl leading-none">&times;</button>
        </div>

        <div className="px-6 py-4 space-y-4">
          <p className="text-sm text-neutral-600">
            Select the payments to include in this deposit. This will create a single deposit in QuickBooks matching your physical bank deposit.
          </p>

          {/* Deposit date */}
          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-1">Deposit Date</label>
            <input
              type="date"
              value={depositDate}
              onChange={e => setDepositDate(e.target.value)}
              className="w-full px-3 py-2 border border-neutral-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
            />
          </div>

          {/* Order list */}
          <div className="border border-neutral-200 rounded-lg divide-y divide-neutral-100 max-h-64 overflow-y-auto">
            {sorted.map(o => (
              <label key={o.order_id} className="flex items-start gap-3 px-3 py-2.5 hover:bg-neutral-50 cursor-pointer">
                <input
                  type="checkbox"
                  checked={checkedIds.has(o.order_id)}
                  onChange={() => toggle(o.order_id)}
                  className="mt-0.5 rounded border-neutral-300"
                />
                <div className="flex-1 min-w-0 text-sm">
                  <div className="font-medium text-neutral-800 truncate">{o.title_company}</div>
                  <div className="text-neutral-500 text-xs truncate">{o.appointment_name}</div>
                  {o.ar_check_number && (
                    <div className="text-neutral-400 text-xs">Check #{o.ar_check_number}</div>
                  )}
                </div>
                <div className="shrink-0 text-sm font-medium text-neutral-700">
                  ${(o.ar_amount || 0).toFixed(2)}
                </div>
              </label>
            ))}
          </div>

          {/* Total */}
          <div className="flex justify-between text-sm font-semibold text-neutral-800 border-t border-neutral-200 pt-3">
            <span>{selected.length} payment{selected.length !== 1 ? 's' : ''} selected</span>
            <span>${total.toFixed(2)}</span>
          </div>
        </div>

        <div className="flex justify-end gap-2 px-6 py-4 border-t border-neutral-200">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-neutral-700 bg-neutral-100 hover:bg-neutral-200 rounded-lg transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={() => onConfirm(selected.map(o => o.order_id), depositDate)}
            disabled={selected.length === 0}
            className="px-4 py-2 text-sm font-medium text-white bg-green-600 hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg transition-colors"
          >
            Create Deposit
          </button>
        </div>
      </div>
    </div>
  );
};

export default AdminFinancialsDepositModal;
